import { UseFormReturn } from "react-hook-form";
import { DrawerClose, DrawerHeader } from "@/components/ui/drawer";
import { Step } from "./constants";
import { cn, formatDate } from "@/lib/utils";
import { CaretRight } from "@phosphor-icons/react";
import { Button, buttonVariants } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import ScrollableCalendar from "@/components/custom/scrollable-calendar";
import { ACCOUNT_TYPES } from "@/lib/constants";
import { Currency, Account } from "@/lib/types";
import { TransferConversionRateField, TransferFormValues } from "./";
import { FormField } from "@/components/ui/form";
import FilePreview from "@/components/custom/file-preview";

const InitialStep = ({
  form,
  setStep,
  currencies,
  accounts,
  title,
  toAccount,
  fromAccount,
  setConversionRateField,
  fromAccountConversionRate,
  toAccountConversionRate,
  isSubmitting,
}: {
  form: UseFormReturn<TransferFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  currencies: Currency[];
  accounts: Account[];
  title?: string;
  toAccount: Account | null;
  fromAccount: Account | null;
  setConversionRateField: React.Dispatch<
    React.SetStateAction<TransferConversionRateField>
  >;
  /** Current from-leg rate from useConversionRate. */
  fromAccountConversionRate: number | undefined;
  /** Current to-leg rate from useConversionRate. */
  toAccountConversionRate: number | undefined;
  /** Disables Save while a submission is in flight to prevent duplicate transfers. */
  isSubmitting?: boolean;
}) => {
  const { t } = useTranslation();
  const transactionCurrency = currencies.find(
    (c) => c.id === form.watch("currencyId")
  );

  return (
    <>
      <DrawerHeader
        title={title ?? t("title.add_transfer")}
        leftButton={
          <DrawerClose asChild>
            <button
              type="button"
              className="text-[15px] font-medium leading-5 text-[#45D3EC]"
            >
              {t("button.cancel")}
            </button>
          </DrawerClose>
        }
        rightButton={
          <button
            data-testid="transfer-form-submit-button"
            type="submit"
            disabled={isSubmitting}
            className="text-[15px] font-medium leading-5 text-[#45D3EC] disabled:opacity-50"
          >
            {t("button.save")}
          </button>
        }
      />

      <div className="mt-[30px] w-full space-y-[15px]">
        <AmountButton form={form} setStep={setStep} />
        <CurrencyButton currencies={currencies} form={form} setStep={setStep} />
        <FromAccountButton accounts={accounts} form={form} setStep={setStep} />
        <ToAccountButton accounts={accounts} form={form} setStep={setStep} />
        {fromAccount &&
          form.watch("currencyId") &&
          fromAccount.currencyId !== form.watch("currencyId") && (
            <ExchangeRateButton
              form={form}
              setStep={setStep}
              setConversionRateField={setConversionRateField}
              rateField="fromAccountConversionRate"
              data-testid="transfer-form-from-account-exchange-rate-button"
              fromCurrency={transactionCurrency}
              toCurrency={currencies.find(
                (c) => c.id === fromAccount?.currencyId
              )}
              exchangeRate={fromAccountConversionRate}
            />
          )}
        {toAccount &&
          form.watch("currencyId") &&
          toAccount.currencyId !== form.watch("currencyId") && (
            <ExchangeRateButton
              form={form}
              setStep={setStep}
              setConversionRateField={setConversionRateField}
              rateField="toAccountConversionRate"
              data-testid="transfer-form-exchange-rate-button"
              fromCurrency={transactionCurrency}
              toCurrency={currencies.find(
                (c) => c.id === toAccount?.currencyId
              )}
              exchangeRate={toAccountConversionRate}
            />
          )}
        {fromAccount &&
          toAccount &&
          !form.watch("currencyId") &&
          fromAccount.currencyId !== toAccount.currencyId && (
            <ExchangeRateButton
              form={form}
              setStep={setStep}
              setConversionRateField={setConversionRateField}
              rateField="toAccountConversionRate"
              data-testid="transfer-form-exchange-rate-button"
              fromCurrency={currencies.find(
                (c) => c.id === fromAccount?.currencyId
              )}
              toCurrency={currencies.find(
                (c) => c.id === toAccount?.currencyId
              )}
              exchangeRate={toAccountConversionRate}
            />
          )}
        <DateButton form={form} />
        <NoteButton form={form} setStep={setStep} />
        <UploadFilesButton form={form} />
      </div>
    </>
  );
};

export default InitialStep;

const CurrencyButton = ({
  currencies,
  form,
  setStep,
}: {
  currencies: Currency[];
  form: UseFormReturn<TransferFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const { t } = useTranslation();

  const currency = useMemo(
    () =>
      currencies.find((currency) => currency.id === form.watch("currencyId")),
    [currencies, form]
  );

  return (
    <>
      {form.getValues("currencyId") ? (
        <button
          data-testid="transfer-form-currency-button"
          type="button"
          onClick={() => setStep(Step.CURRENCY)}
          className="flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
        >
          <span className="text-xs leading-4 text-white/50">
            {t("label.currency")}
          </span>
          <p className="flex items-center gap-1.5 text-start text-[15px] text-white">
            <span>{currency?.symbol}</span>
            <span>{t(`currency.${currency?.code.toLocaleLowerCase()}`)}</span>
          </p>
        </button>
      ) : (
        <Button
          data-invalid={!!form.formState.errors.currencyId}
          data-testid="transfer-form-currency-button"
          onClick={() => setStep(Step.CURRENCY)}
          size="lg"
          variant="outline"
          className={cn("flex w-full items-center justify-between gap-2", {
            "border-red-500 hover:border-red-500 active:border-red-500":
              form.formState.errors.currencyId,
          })}
        >
          {t("label.currency")}
          <CaretRight width={10} color="#E3EDFC" />
        </Button>
      )}
    </>
  );
};

const FromAccountButton = ({
  form,
  setStep,
  accounts,
}: {
  form: UseFormReturn<TransferFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  accounts: Account[];
}) => {
  const { t } = useTranslation();

  const fromAccount = useMemo(
    () =>
      accounts.find((account) => account.id === form.watch("fromAccountId")),
    [accounts, form]
  );

  return (
    <>
      {fromAccount?.accountNumber ? (
        <button
          data-testid="transfer-form-from-account-button"
          type="button"
          onClick={() => setStep(Step.FROM_ACCOUNT)}
          className="flex w-full items-center justify-between rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
        >
          <div className="flex flex-col items-start justify-between">
            <span className="text-xs leading-4 text-white/50">
              {t("label.from_account")}
            </span>
            <p className="flex items-center gap-1 text-[15px] text-white">
              <span>
                {fromAccount?.type === "Credit Card"
                  ? t("label.credit_card")
                  : t("label.debit_card")}{" "}
              </span>

              <span>/</span>

              <span>{fromAccount?.accountNumber}</span>
            </p>
          </div>
          <span className="font-semibold text-white">
            {t(
              `account_type.${
                ACCOUNT_TYPES.find(
                  (accountType) => accountType.value === fromAccount?.type
                )?.translationKey
              }`
            )}
          </span>
        </button>
      ) : (
        <Button
          data-invalid={!!form.formState.errors.fromAccountId}
          data-testid="transfer-form-from-account-button"
          onClick={() => setStep(Step.FROM_ACCOUNT)}
          size="lg"
          className={cn(
            "flex w-full flex-row items-center justify-between gap-2",
            {
              "border-red-500 hover:border-red-500 active:border-red-500":
                form.formState.errors.fromAccountId,
            }
          )}
          variant="outline"
        >
          {t("label.from_account")}
          <CaretRight size={10} color="#E3EDFC" />
        </Button>
      )}
    </>
  );
};

const ToAccountButton = ({
  form,
  setStep,
  accounts,
}: {
  form: UseFormReturn<TransferFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  accounts: Account[];
}) => {
  const { t } = useTranslation();

  const toAccount = useMemo(
    () => accounts.find((account) => account.id === form.watch("toAccountId")),
    [accounts, form]
  );

  return (
    <>
      {toAccount?.accountNumber ? (
        <button
          data-testid="transfer-form-to-account-button"
          type="button"
          onClick={() => setStep(Step.TO_ACCOUNT)}
          className="flex w-full items-center justify-between rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
        >
          <div className="flex flex-col items-start justify-between">
            <span className="text-xs leading-4 text-white/50">
              {t("label.to_account")}
            </span>
            <p className="flex items-center gap-1 text-[15px] text-white">
              <span>
                {toAccount?.type === "Credit Card"
                  ? t("label.credit_card")
                  : t("label.debit_card")}{" "}
              </span>

              <span>/</span>

              <span>{toAccount?.accountNumber}</span>
            </p>
          </div>
          <span className="font-semibold text-white">
            {t(
              `account_type.${
                ACCOUNT_TYPES.find(
                  (accountType) => accountType.value === toAccount?.type
                )?.translationKey
              }`
            )}
          </span>
        </button>
      ) : (
        <Button
          data-invalid={!!form.formState.errors.toAccountId}
          data-testid="transfer-form-to-account-button"
          onClick={() => setStep(Step.TO_ACCOUNT)}
          size="lg"
          className={cn(
            "flex w-full flex-row items-center justify-between gap-2",
            {
              "border-red-500 hover:border-red-500 active:border-red-500":
                form.formState.errors.toAccountId,
            }
          )}
          variant="outline"
        >
          {t("label.to_account")}
          <CaretRight size={10} color="#E3EDFC" />
        </Button>
      )}
    </>
  );
};

const DateButton = ({ form }: { form: UseFormReturn<TransferFormValues> }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative w-full">
      <div
        data-invalid={!!form.formState.errors.transactionDate}
        data-testid="transfer-form-date-button"
        role="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          form.getValues("transactionDate")
            ? "flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
            : `flex w-full flex-row items-center !justify-between gap-2 ${buttonVariants(
                {
                  variant: "outline",
                  size: "lg",
                }
              )}`,
          {
            "border-red-500 hover:border-red-500 active:border-red-500":
              form.formState.errors.transactionDate,
          }
        )}
      >
        {form.getValues("transactionDate") ? (
          <>
            <span className="text-xs leading-4 text-white/50">
              {t("label.date")}
            </span>
            <span className="text-start text-[15px] text-white">
              {formatDate(
                form.getValues("transactionDate")!,
                "dd MMMM yyyy - HH:mm"
              )}
            </span>
          </>
        ) : (
          <div className="flex w-full flex-row items-center justify-between gap-2">
            {t("label.date")}
            <CaretRight width={10} color="#E3EDFC" />
          </div>
        )}
      </div>

      {isOpen && (
        <ScrollableCalendar
          data-testid="transfer-form-date-calendar"
          date={
            form.getValues("transactionDate")
              ? new Date(form.getValues("transactionDate")!)
              : new Date()
          }
          setDate={(date) => {
            form.setValue("transactionDate", date);
            form.trigger("transactionDate");
          }}
        />
      )}
    </div>
  );
};

const NoteButton = ({
  form,
  setStep,
}: {
  form: UseFormReturn<TransferFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const { t } = useTranslation();
  return (
    <>
      {form.getValues("description") &&
      form.getValues("description").trim() !== "" ? (
        <button
          data-testid="transfer-form-note-button"
          type="button"
          onClick={() => setStep(Step.NOTE)}
          className="flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
        >
          <span className="text-xs leading-4 text-white/50">
            {t("label.notes")}
          </span>
          <p className="text-start text-[15px] text-white">
            {form.getValues("description")}
          </p>
        </button>
      ) : (
        <Button
          data-invalid={!!form.formState.errors.description}
          data-testid="transfer-form-note-button"
          onClick={() => setStep(Step.NOTE)}
          size="lg"
          variant="outline"
          className={cn("w-full", {
            "border-red-500 hover:border-red-500 active:border-red-500":
              form.formState.errors.description,
          })}
        >
          <div className="flex w-full flex-row items-center justify-start gap-2">
            {t("label.add_note")}
            <span className="text-base text-white/50">
              ({t("label.optional")})
            </span>
          </div>
          <CaretRight width={10} color="#E3EDFC" />
        </Button>
      )}
    </>
  );
};

const AmountButton = ({
  form,
  setStep,
}: {
  form: UseFormReturn<TransferFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const { t } = useTranslation();
  return (
    <>
      {form.getValues("amount") ? (
        <button
          data-testid="transfer-form-amount-button"
          type="button"
          onClick={() => setStep(Step.AMOUNT)}
          className="flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
        >
          <span className="text-xs leading-4 text-white/50">
            {t("label.amount")}
          </span>
          <p className="text-start text-[15px] text-white">
            {form.getValues("amount")}
          </p>
        </button>
      ) : (
        <Button
          data-invalid={!!form.formState.errors.amount}
          data-testid="transfer-form-amount-button"
          onClick={() => setStep(Step.AMOUNT)}
          size="lg"
          variant="outline"
          className={cn("w-full", {
            "border-red-500 hover:border-red-500 active:border-red-500":
              form.formState.errors.amount,
          })}
        >
          <div className="flex w-full flex-row items-center justify-start gap-2">
            {t("label.add_amount")}
          </div>
          <CaretRight width={10} color="#E3EDFC" />
        </Button>
      )}
    </>
  );
};

const ExchangeRateButton = ({
  form,
  setStep,
  setConversionRateField,
  rateField,
  "data-testid": testId,
  fromCurrency,
  toCurrency,
  exchangeRate,
}: {
  form: UseFormReturn<TransferFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  setConversionRateField: React.Dispatch<
    React.SetStateAction<TransferConversionRateField>
  >;
  rateField: TransferConversionRateField;
  "data-testid": string;
  fromCurrency?: Currency;
  toCurrency?: Currency;
  /** Display value sourced from useConversionRate (not form state) so a context change updates the display in the same paint. */
  exchangeRate: number | undefined;
}) => {
  const { t } = useTranslation();
  const fromCode = fromCurrency?.code || "???";
  const toCode = toCurrency?.code || "???";
  const error = form.formState.errors[rateField];

  const openExchangeRateStep = () => {
    setConversionRateField(rateField);
    setStep(Step.EXCHANGE_RATE);
  };

  // Format the rate display showing both directions
  const formatRateDisplay = () => {
    if (!exchangeRate || exchangeRate <= 0) return null;
    const inverseRate = 1 / exchangeRate;
    return (
      <div className="flex flex-col items-start gap-0.5">
        <span className="text-[15px] text-white">
          1 {toCode} = {inverseRate.toFixed(6)} {fromCode}
        </span>
        <span className="text-xs text-white/50">
          1 {fromCode} = {exchangeRate.toFixed(6)} {toCode}
        </span>
      </div>
    );
  };

  return (
    <>
      {exchangeRate ? (
        <button
          data-testid={testId}
          type="button"
          onClick={openExchangeRateStep}
          className="flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
        >
          <span className="text-xs leading-4 text-white/50">
            {t("label.exchange_rate")}
          </span>
          {formatRateDisplay()}
        </button>
      ) : (
        <Button
          data-invalid={!!error}
          data-testid={testId}
          onClick={openExchangeRateStep}
          size="lg"
          variant="outline"
          className={cn("w-full", {
            "border-red-500 hover:border-red-500 active:border-red-500": error,
          })}
        >
          <div className="flex w-full flex-row items-center justify-start gap-2">
            {t("label.add_exchange_rate")} ({fromCode} → {toCode})
          </div>
          <CaretRight width={10} color="#E3EDFC" />
        </Button>
      )}
    </>
  );
};

const UploadFilesButton = ({
  form,
}: {
  form: UseFormReturn<TransferFormValues>;
}) => {
  const { t } = useTranslation();

  return (
    <>
      <label
        data-invalid={!!form.formState.errors.files}
        data-testid="transfer-form-files-label"
        htmlFor="transfer-form-files-input"
        className={cn(
          form.getValues("files") &&
            (form.getValues("files") as unknown as File[]).length > 0
            ? "flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
            : `flex w-full flex-row items-center justify-between gap-2 ${buttonVariants(
                {
                  variant: "outline",
                  size: "lg",
                }
              )}`,
          {
            "border-red-500 hover:border-red-500 active:border-red-500":
              form.formState.errors.files,
          }
        )}
      >
        {form.getValues("files") &&
        (form.getValues("files") as unknown as File[]).length > 0 ? (
          <>
            <span className="text-xs leading-4 text-white/50">
              {t("label.files")}
            </span>
            <FilePreview
              className="mt-1"
              files={
                "url" in form.watch("files")[0]
                  ? undefined
                  : (form.watch("files") as unknown as File[])
              }
              urls={
                "url" in form.watch("files")[0]
                  ? (form.watch("files") as { id: string; url: string }[]).map(
                      (file) => file.url
                    )
                  : undefined
              }
              onRemove={(file) => {
                if (typeof file === "string") {
                  form.setValue(
                    "files",
                    (
                      form.watch("files") as { id: string; url: string }[]
                    ).filter((f) => f.url !== file)
                  );
                } else {
                  form.setValue(
                    "files",
                    (form.watch("files") as unknown as File[]).filter(
                      (f) => f !== file
                    ) as unknown as { id: string; url: string }[]
                  );
                }
              }}
            />
          </>
        ) : (
          <div className="flex w-full flex-row items-center justify-between gap-2">
            {t("label.files")}
            <CaretRight width={10} color="#E3EDFC" />
          </div>
        )}
      </label>
      <FormField
        control={form.control}
        name="files"
        render={({ field: { value, onChange, ...rest } }) => (
          <input
            data-testid="transfer-form-files-input"
            id="transfer-form-files-input"
            type="file"
            multiple
            accept="image/*,application/pdf"
            hidden
            onChange={(e) => {
              form.watch("files");
              onChange(Array.from(e.target.files || []));
            }}
            {...rest}
          />
        )}
      />
    </>
  );
};
